import {Component, ElementRef, OnInit, ViewChild, ChangeDetectorRef} from '@angular/core';
import {Title} from '@angular/platform-browser';
// Switched from mock FixedAssetsService to API-backed reports service
import {FixedAssetReportsApiService, GlEntriesFilters} from '../../../../Services/fixed-asset-reports-api.service';
import {FixedAssetModel} from '../../../../DataModels/fixed-asset-model';
import {DataExportService} from '../../../../Services/dataExport.service';
import {NotificationService} from '../../../../Services/notification.service';
import {EmailModel} from '../../../../DataModels/email-model';
import {Modal} from 'bootstrap';
import {DateFilterService} from '../../../../Services/dateFilter.service';
import {DataService} from '../../../../Services/data.service';
import {ResponseModel} from '../../../../DataModels/response-model';
import {ReportPrintService} from '../../../../Services/report-print.service';

@Component({
  selector: 'app-fixed-asset-gl-entries',
  templateUrl: './gl-entries.component.html',
  styleUrls: ['./gl-entries.component.css'],
  standalone: false
})
export class FixedAssetGlEntriesComponent implements OnInit {
  data: { asset: FixedAssetModel, currentYearDep: number }[] = [];
  // Lightweight account lookup for friendly rendering
  private accountMap = new Map<string, { number: string; name: string }>();
  vm: any = {
    showFilters: false,
    filtered: false,
    buttonsDisabled: false,
    report: [],
    report_format: false,
    monthCount: 0,
    showSpecificDates: false,
    company: [],
    location: [],
    request: {
      fromDate: null,
      toDate: null,
      company_id: '',
      location_id: ''
    },
  };
  buttonConfig: any = {
    reportFormat: false,
    // single print button
    print: false,
    printReport: true,
    email: true,
    update: true,
    exportExcel: true,
    close: true,
  };
  @ViewChild('tableRef') tableRef?: ElementRef<HTMLTableElement>;
  @ViewChild('printArea') printArea?: ElementRef<HTMLElement>;
  reportEmail: EmailModel = new EmailModel();

  constructor(
    private title: Title,
    private reportsApi: FixedAssetReportsApiService,
    private dataExport: DataExportService,
    private notify: NotificationService,
    private cdr: ChangeDetectorRef,
    private dateFilter: DateFilterService,
    private tableDataService: DataService,
    private reportPrint: ReportPrintService,
  ) {
  }

  ngOnInit(): void {
    this.title.setTitle($localize`:Fixed Asset GL Entries@@faGl:Fixed Asset GL Entries`);
    // initialize from/to date lookups similar to Trial Balance
    try {
      const ret = this.dateFilter.DateFilterInit(this.vm.request.toDate, this.vm.request.fromDate);
      (this.vm as any).fromDateSelect = ret.fromDateSelect;
      (this.vm as any).toDateSelect = ret.toDateSelect;
      this.vm.monthCount = ret.monthCount;
    } catch {
    }
    // Load Accounts once for rendering "Number - Name"
    try {
      this.tableDataService
        .getApiListData('account', '?state=active&pageNumber=0&pageSize=999999')
        .subscribe((result: ResponseModel) => {
          const accounts = (result as any)?.Data ?? result;
          const map = new Map<string, { number: string; name: string }>();
          (accounts as any[]).forEach((a: any) => {
            const id = String(a?.id);
            if (!id) {
              return;
            }
            map.set(id, {number: String(a.number ?? ''), name: String(a.name ?? '')});
          });
          this.accountMap = map;
        });
    } catch {
    }
    // Populate Company and Location lookups
    try {
      this.tableDataService.getData('company?state=active&pageNumber=0&pageSize=999999')
        .subscribe((result: ResponseModel) => {
          (this.vm as any).company = (result as any)?.Data ?? result;
        });
      this.tableDataService.getData('location?state=active&pageNumber=0&pageSize=999999')
        .subscribe((result: ResponseModel) => {
          (this.vm as any).location = (result as any)?.Data ?? result;
        });
    } catch {
    }
    // Initial fetch
    this.fetchGlEntries();
  }

  // Render helper: "number - name" with fallback to id
  renderAccount(id?: string): string {
    if (!id) {
      return '';
    }
    const a = this.accountMap.get(String(id));
    if (a) {
      // Avoid trailing dash if one part is missing
      const parts = [a.number, a.name].filter(p => !!p && String(p).trim().length > 0);
      return parts.length ? parts.join(' - ') : String(id);
    }
    return String(id);
  }

  update(): void {
    this.vm.filtered = true;
    this.fetchGlEntries();
  }

  clear(): void {
    this.vm.filtered = false;
    this.vm.request = {fromDate: null, toDate: null, company_id: '', location_id: ''};
    this.fetchGlEntries();
  }

  close(): void {
  }

  onPrint(): void {
    const reportName = $localize`:Fixed Asset GL Entries@@faGl:Fixed Asset GL Entries`;
    const rawHtml = this.printArea?.nativeElement?.outerHTML || this.tableRef?.nativeElement?.outerHTML || '';
    const content = this.reportPrint.cleanContentHtml(rawHtml);
    const html = this.reportPrint.buildHtml({title: reportName, contentHtml: content, orientation: 'landscape'});
    this.reportPrint.printInIframe(html);
  }

  onExport(): void {
    const el = this.tableRef?.nativeElement;
    if (el) {
      this.dataExport.exportToExcel(el, 'fixed-asset-gl-entries.xlsx', 'FA GL Entries');
    } else {
      this.notify.bsWarning($localize`:Nothing to export@@nothingToExport:Nothing to export`);
    }
  }

  onEmail(): void {
    const emailDocObj = new EmailModel();
    const reportName = $localize`:Fixed Asset GL Entries@@faGl:Fixed Asset GL Entries`;

    emailDocObj.print_orientation = '1';
    emailDocObj.convert_attachment_to = 'pdf';
    emailDocObj.fromEmail = '';
    emailDocObj.fromName = 'AcctXErp';
    emailDocObj.header_id = '';
    emailDocObj.toEmail = '';
    emailDocObj.body = $localize`:EmailBody@@emailBody:Please see the attached ${reportName}.` +
      '<br><br><br>Best regards,<br><br>AcctXErp<br><br>';
    emailDocObj.Subject = 'AcctXErp ' + reportName;

    try {
      this.cdr.detectChanges();
    } catch {
    }
    try {
      const html = this.printArea?.nativeElement?.outerHTML || this.tableRef?.nativeElement?.outerHTML || '';
      emailDocObj.pdfHtml = this.reportPrint.buildHtml({
        title: reportName,
        contentHtml: this.reportPrint.cleanContentHtml(html),
        orientation: 'landscape'
      });
    } catch {
      emailDocObj.pdfHtml = '';
    }

    const now = new Date();
    const yyyy = String(now.getFullYear());
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    emailDocObj.FileName = `FA_GL_Entries_${yyyy}${mm}${dd}.pdf`;
    emailDocObj.activity_message = reportName;
    this.reportEmail = emailDocObj;

    const element = document.getElementById('sendEmailModal') as HTMLElement;
    if (element) {
      const emailModal = new Modal(element);
      // Re-enable header buttons when modal closes
      try {
        element.addEventListener('hidden.bs.modal', () => {
          this.vm.buttonsDisabled = false;
          this.vm.report = this.data && this.data.length ? this.data : [{}];
        }, {once: true});
      } catch {
      }
      emailModal.show();
    }
  }

  // --- helpers ---
  private fetchGlEntries(): void {
    const filters: GlEntriesFilters = {
      fromDate: this.vm.request?.fromDate || undefined,
      toDate: this.vm.request?.toDate || undefined,
      companyId: this.vm.request?.company_id || undefined,
      locationId: this.vm.request?.location_id || undefined,
      sortBy: 'AssetName',
      sortDir: 'asc'
    };
    // No paging UI here; request a large page
    const page = 1;
    const pageSize = 999999;
    this.reportsApi.getGlEntries(page, pageSize, filters).subscribe({
      next: ({items}) => {
        // Map API rows into the view model expected by the template
        const rows: { asset: FixedAssetModel, currentYearDep: number }[] = (items || []).map(x => {
          const asset: FixedAssetModel = {
            id: x.assetId,
            name: x.assetName,
            depreciationExpenseAccountId: x.expenseAccountId as any,
            accumulatedDepreciationAccountId: x.accumAccountId as any,
          } as any;
          return {asset, currentYearDep: Number(x.amount || 0)};
        });
        this.data = rows;
        this.vm.report = this.data && this.data.length ? this.data : [{}];
        try {
          this.cdr.detectChanges();
        } catch {
        }
      },
      error: (err) => {
        // Interceptor shows toast; keep UI usable and log once
        try {
          console.log('[FA GL Entries][fetch error]', err);
        } catch {
        }
        this.data = [];
        this.vm.report = [];
        try {
          this.cdr.detectChanges();
        } catch {
        }
      }
    });
  }

}
